import React, { useState } from 'react';
import { Users, Package, Calendar, DollarSign, AlertTriangle, TrendingUp, Gamepad2, Table2, Bell, ArrowRight } from 'lucide-react';
import { createTextPicker, pickLocalizedText } from '../../utils/localizedContent';

export default function AdminDashboard({ stats, lang, onNavigate, billingIssuesCount = 0 }) {
  const [stockThreshold, setStockThreshold] = useState(3);
  const l = createTextPicker(lang);

  if (!stats) {
    return (
      <div className="text-center py-12 text-[#A1A1AA]">
        <p className="text-sm">{pickLocalizedText(lang, 'Sin datos disponibles o sin permiso de acceso.', 'No data available or insufficient permissions.')}</p>
      </div>
    );
  }

  const StatCard = ({ icon, label, value, color, bgColor, suffix = '', alert = false, tab = null }) => (
    <div
      onClick={() => tab && onNavigate && onNavigate(tab)}
      className={`rounded-2xl p-5 flex items-center gap-4 transition-all ${
        alert ? 'bg-[#EF4444]/10 border border-[#EF4444]/20' : 'bg-[#12121A] border border-white/5'
      } ${tab ? 'cursor-pointer hover:border-[#C9A962]/40 hover:scale-[1.01]' : ''}`}
    >
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${bgColor}`}>
        {React.cloneElement(icon, { size: 22, className: color })}
      </div>
      <div className="flex-1">
        <p className={`text-2xl font-black ${alert ? 'text-[#EF4444]' : 'text-white'}`}>{value}{suffix}</p>
        <p className="text-[#A1A1AA] text-sm">{label}</p>
      </div>
      {tab && <ArrowRight size={14} className="text-[#52525B]" />}
    </div>
  );

  const lowStockProducts = (stats.low_stock_products || []).filter(p => p.stock <= stockThreshold);

  return (
    <div className="space-y-6">
      {billingIssuesCount > 0 && (
        <button
          type="button"
          onClick={() => onNavigate && onNavigate('billing')}
          className="w-full text-left bg-[#EF4444]/10 border border-[#EF4444]/25 rounded-2xl p-4 hover:border-[#EF4444]/45 transition-colors"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle size={18} className="text-[#F87171] mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-[#FCA5A5] font-semibold text-sm">
                {l(
                  `Hay ${billingIssuesCount} incidencia${billingIssuesCount > 1 ? 's' : ''} de facturacion pendiente${billingIssuesCount > 1 ? 's' : ''}.`,
                  `There ${billingIssuesCount > 1 ? 'are' : 'is'} ${billingIssuesCount} pending billing issue${billingIssuesCount > 1 ? 's' : ''}.`
                )}
              </p>
              <p className="text-[#FCA5A5]/80 text-xs mt-1">
                {l('Pulsa para abrir Facturacion y revisarlas.', 'Click to open Billing and review them.')}
              </p>
            </div>
            <ArrowRight size={14} className="text-[#FCA5A5]/80 mt-1 flex-shrink-0" />
          </div>
        </button>
      )}

      {/* Main stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={<Users />} label={l('Usuarios', 'Users')}
          value={stats.users_count ?? 0} color="text-[#3B82F6]" bgColor="bg-[#3B82F6]/10" tab="users" />
        <StatCard icon={<Package />} label={l('Productos', 'Products')}
          value={stats.products_count ?? 0} color="text-[#10B981]" bgColor="bg-[#10B981]/10" tab="products" />
        <StatCard icon={<Calendar />} label={l('Eventos', 'Events')}
          value={stats.events_count ?? 0} color="text-[#F59E0B]" bgColor="bg-[#F59E0B]/10" tab="events" />
        <StatCard icon={<DollarSign />} label={l('Ingresos Total', 'Total Revenue')}
          value={(stats.total_revenue ?? 0).toFixed(0)} suffix="€" color="text-[#C9A962]" bgColor="bg-[#C9A962]/10" tab="billing" />
      </div>

      {/* Pending alerts */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={<Gamepad2 />} label={l('Reservas Juego Pend.', 'Pending Game Res.')}
          value={stats.pending_game_reservations ?? 0} color="text-[#8B5CF6]" bgColor="bg-[#8B5CF6]/10"
          alert={(stats.pending_game_reservations ?? 0) > 0} tab="gameRes" />
        <StatCard icon={<Table2 />} label={l('Reservas Mesa Pend.', 'Pending Table Res.')}
          value={stats.pending_table_reservations ?? 0} color="text-[#06B6D4]" bgColor="bg-[#06B6D4]/10"
          alert={(stats.pending_table_reservations ?? 0) > 0} tab="tableRes" />
        <StatCard icon={<Bell />} label={l('Reservas Prod. Pend.', 'Pending Prod. Res.')}
          value={stats.pending_product_reservations ?? 0} color="text-[#F97316]" bgColor="bg-[#F97316]/10"
          alert={(stats.pending_product_reservations ?? 0) > 0} tab="reservations" />
        <StatCard icon={<TrendingUp />} label={l('Total Pedidos', 'Total Orders')}
          value={stats.orders_count ?? 0} color="text-[#10B981]" bgColor="bg-[#10B981]/10" tab="orders" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Upcoming events */}
        <div className="bg-[#12121A] border border-white/5 rounded-2xl p-5 flex flex-col">
          <h3 className="text-white font-bold text-sm mb-3 flex items-center gap-2 flex-shrink-0">
            <Calendar size={15} className="text-[#C9A962]" />{l('Próximos Eventos', 'Upcoming Events')}
          </h3>
          <div className="overflow-y-auto max-h-56 space-y-2 flex-1">
            {(stats.upcoming_events || []).slice(0, 10).length === 0 ? (
              <p className="text-[#52525B] text-xs">{l('Sin eventos próximos', 'No upcoming events')}</p>
            ) : (
              (stats.upcoming_events || []).slice(0, 10).map(ev => (
                <div key={ev.event_id} className="flex items-center justify-between py-1">
                  <div>
                    <p className="text-white text-xs font-medium truncate max-w-[160px]">{ev.title}</p>
                    <p className="text-[#52525B] text-[10px]">{ev.date} {ev.time}</p>
                  </div>
                  <span className="text-[#A1A1AA] text-xs flex-shrink-0">
                    {(ev.registered_participants || []).length}/{ev.max_participants}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Low stock */}
        <div className="bg-[#12121A] border border-white/5 rounded-2xl p-5 flex flex-col">
          <div className="flex items-center justify-between mb-3 flex-shrink-0">
            <h3 className="text-white font-bold text-sm flex items-center gap-2">
              <AlertTriangle size={15} className="text-[#F59E0B]" />{l('Stock Bajo', 'Low Stock')}
            </h3>
            <div className="flex items-center gap-1.5">
              <span className="text-[#52525B] text-[10px]">≤</span>
              <input
                type="number" min="1" max="50"
                value={stockThreshold}
                onChange={e => setStockThreshold(Math.max(1, parseInt(e.target.value) || 1))}
                className="w-12 bg-[#1C1C28] border border-white/10 text-white rounded-lg px-2 py-1 text-xs text-center outline-none focus:border-[#C9A962]"
              />
              <span className="text-[#52525B] text-[10px]">{l('u.', 'u.')}</span>
            </div>
          </div>
          <div className="overflow-y-auto max-h-56 space-y-1.5 flex-1">
            {lowStockProducts.length === 0 ? (
              <p className="text-[#52525B] text-xs">{l('Sin productos con stock bajo', 'No low stock products')}</p>
            ) : (
              lowStockProducts.slice(0, 10).map(p => (
                <div key={p.product_id} className="flex items-center justify-between py-0.5">
                  <p className="text-white text-xs font-medium truncate max-w-[170px]">{p.name}</p>
                  <span className={`text-xs font-bold flex-shrink-0 ${p.stock === 0 ? 'text-[#EF4444]' : p.stock <= 1 ? 'text-[#EF4444]' : 'text-[#F59E0B]'}`}>
                    {p.stock}u
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Popular games */}
        <div className="bg-[#12121A] border border-white/5 rounded-2xl p-5 flex flex-col">
          <h3 className="text-white font-bold text-sm mb-3 flex items-center gap-2 flex-shrink-0">
            <Gamepad2 size={15} className="text-[#C9A962]" />{l('Juegos Más Reservados', 'Most Reserved Games')}
          </h3>
          <div className="overflow-y-auto max-h-56 space-y-1.5 flex-1">
            {(stats.popular_games || []).length === 0 ? (
              <p className="text-[#52525B] text-xs">{l('Sin datos de reservas', 'No reservation data')}</p>
            ) : (
              (stats.popular_games || []).slice(0, 10).map((g, i) => (
                <div key={g._id || i} className="flex items-center justify-between py-0.5">
                  <p className="text-white text-xs font-medium truncate max-w-[170px]">{g._id}</p>
                  <span className="text-[#C9A962] text-xs font-bold flex-shrink-0">{g.count}x</span>
                </div>
              ))
            )}
          </div>
        </div>

      </div>
    </div>
  );
}
